const CHUNK_RELOAD_SESSION_KEY = 'sky-crm:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

// REG-963: every wording browsers use for a lazy chunk that did not load.
// Safari reports an HTML response as "'text/html' is not a valid JavaScript
// MIME type"; a stylesheet that did not load is Vite's "Unable to preload CSS".
const CHUNK_LOAD_ERROR_PATTERN = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  /Loading chunk .+ failed/i,
  /Failed to load module script/i,
  /is not a valid JavaScript MIME type/i,
  /Unable to preload CSS/i,
];

let fallbackReloadAttempted = false;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_LOAD_ERROR_PATTERN.some((pattern) => pattern.test(message));
}

/**
 * A tab can keep an old Vite entry bundle open after a deployment removes its
 * hashed lazy chunks. Reload once onto the current index instead of leaving the
 * user in an error boundary. The cooldown prevents a broken deployment from
 * causing an infinite refresh loop.
 */
export function reloadAfterChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;

  const now = Date.now();
  try {
    const previousAttempt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY));
    if (Number.isFinite(previousAttempt) && now - previousAttempt < CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(now));
  } catch {
    if (fallbackReloadAttempted) return false;
    fallbackReloadAttempted = true;
  }

  window.location.reload();
  return true;
}

export function registerChunkLoadRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const preloadError = (event as Event & { payload?: unknown }).payload;
    // REG-963: cancel the event only when the page is actually reloading.
    // A cancelled event makes Vite resolve the import to undefined, and
    // React.lazy then caches "Cannot read properties of undefined (reading
    // 'default')" for the rest of the tab's life: the route stays dead until a
    // hard refresh. Left uncancelled, the real error reaches the error
    // boundary, which offers a reload.
    if (reloadAfterChunkLoadError(preloadError)) event.preventDefault();
  });
}
